import { emptySplitApi } from '../../../../store/emptyApi';

// Type definitions for Metric (definition) - Global, reusable metric definitions
export interface Metric {
  id: number;
  metric_type: number;
  metric_type_display?: string;
  metric_name: string;
  metric_label: string | null;
  is_active: boolean;
  description: string | null;
  created_by: number | null;
  created_by_email?: string | null;
  created_at: string;
  updated_at: string;
}

export interface MetricList {
  id: number;
  metric_type: number;
  metric_type_display?: string;
  metric_name: string;
  metric_label: string | null;
  is_active: boolean;
  created_at: string;
}

export interface MetricCreate {
  metric_type: number;
  metric_name: string;
  metric_label?: string | null;
  is_active?: boolean;
  description?: string | null;
}

export interface MetricUpdate {
  metric_type?: number;
  metric_name?: string;
  metric_label?: string | null;
  is_active?: boolean;
  description?: string | null;
}

export interface MetricListParams {
  metric_type?: number;
  is_active?: boolean;
}

// Route constants for metrics API
export const METRICS_ROUTES = {
  base: '/api/admin/v1/metrics/',
  byId: (id: number) => `/api/admin/v1/metrics/${id}/`,
  export: '/api/admin/v1/metrics/export/',
} as const;

// Tag types for cache invalidation
export const MetricApiTags = {
  Metric: 'Metric',
} as const;

const taggedApi = emptySplitApi.enhanceEndpoints({
  addTagTypes: Object.values(MetricApiTags),
});

export const metricsApi = taggedApi.injectEndpoints({
  endpoints: (build) => ({
    // List metric definitions
    getMetrics: build.query<MetricList[], MetricListParams>({
      query: (params) => {
        // Build params object, only including defined values
        const queryParams: Record<string, string | number | boolean> = {};
        if (params.metric_type !== undefined && params.metric_type !== null) {
          queryParams.metric_type = params.metric_type;
        }
        if (params.is_active !== undefined && params.is_active !== null) {
          queryParams.is_active = params.is_active;
        }
        return {
          url: METRICS_ROUTES.base,
          params: queryParams,
        };
      },
      // Ensure query key includes all filter params for proper cache invalidation
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const { metric_type, is_active } = queryArgs;
        return `${endpointName}(${JSON.stringify({
          metric_type: metric_type ?? null,
          is_active: is_active ?? null,
        })})`;
      },
      providesTags: [MetricApiTags.Metric],
    }),

    // Get single metric definition
    getMetric: build.query<Metric, number>({
      query: (id) => METRICS_ROUTES.byId(id),
      providesTags: (result, error, id) => [
        { type: MetricApiTags.Metric, id },
      ],
    }),

    // Create metric definition
    createMetric: build.mutation<Metric, MetricCreate>({
      query: (data) => ({
        url: METRICS_ROUTES.base,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [MetricApiTags.Metric],
    }),

    // Update metric definition
    updateMetric: build.mutation<
      Metric,
      { id: number; data: MetricUpdate }
    >({
      query: ({ id, data }) => ({
        url: METRICS_ROUTES.byId(id),
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: MetricApiTags.Metric, id },
        MetricApiTags.Metric,
      ],
    }),

    // Partial update metric definition
    partialUpdateMetric: build.mutation<
      Metric,
      { id: number; data: Partial<MetricUpdate> }
    >({
      query: ({ id, data }) => ({
        url: METRICS_ROUTES.byId(id),
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: MetricApiTags.Metric, id },
        MetricApiTags.Metric,
      ],
    }),

    // Delete metric definition
    deleteMetric: build.mutation<void, number>({
      query: (id) => ({
        url: METRICS_ROUTES.byId(id),
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: MetricApiTags.Metric, id },
        MetricApiTags.Metric,
      ],
    }),

    // Export metric definitions
    exportMetrics: build.query<
      Blob | MetricList[],
      { format?: 'csv' | 'json'; params?: MetricListParams }
    >({
      query: ({ format = 'csv', params }) => ({
        url: METRICS_ROUTES.export,
        params: {
          format,
          ...params,
        },
        responseHandler: async (response) => {
          if (format === 'csv') {
            return await response.blob();
          } else {
            return await response.json();
          }
        },
      }),
    }),
  }),
  overrideExisting: false,
});

// Export hooks for use in components
export const {
  useGetMetricsQuery,
  useGetMetricQuery,
  useCreateMetricMutation,
  useUpdateMetricMutation,
  usePartialUpdateMetricMutation,
  useDeleteMetricMutation,
  useLazyExportMetricsQuery,
} = metricsApi;

